import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { FaBarcode, FaCalendarAlt, FaMapMarkerAlt, FaUserTie, FaUserSlash } from 'react-icons/fa';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { CVCard, CVCardContent, EmployeeName, EmployeeRole, ItalicText } from '../styles';
import CVCompletionGaugeCard from './CVCompletionGaugeCard';
import { formatDate } from '../../../utils/dateUtils';
import { COLORS } from '../../../constants/radar';
import { useCVPermissions } from '../../../hooks/useCVPermissions';
import { formatCvCode } from '../../../utils/cvCode';

const CVCardItem = ({ cv, onClick, onView }) => {
  const { canManageCV } = useCVPermissions();

  const handleCardClick = async () => {
    // If user can manage CV, navigate to edit mode
    // Otherwise, navigate to view mode
    const hasAccess = await canManageCV(cv);
    if (hasAccess) {
      if (onClick) onClick(cv);
    } else {
      if (onView) onView(cv);
    }
  };

  return (
    <CVCard key={cv.id} onClick={handleCardClick} style={{ margin: 0, height: '100%', maxWidth: '100%' }}>
      <CVCardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EmployeeAvatar employee={cv.employee} size={40} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmployeeName>{cv.fullName}</EmployeeName>
                <Tooltip title={cv.employee ? "Linked with individual" : "Not linked with individual"}>
                  <Chip
                    icon={cv.employee ? <FaUserTie size={8} /> : <FaUserSlash size={8} />}
                    label={cv.employee ? "Linked" : "Unlinked"}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '0.6rem',
                      mb: 0.5,
                      bgcolor: cv.employee ? '#E0F2FE' : '#FEE2E2',
                      color: cv.employee ? '#0369A1' : '#B91C1C',
                      border: cv.employee ? '1px solid #BAE6FD' : '1px solid #FECACA',
                    }}
                  />
                </Tooltip>
              </Box>
              <EmployeeRole>
                {cv.professionalDetails?.useCustomClassification ? (
                  cv.professionalDetails?.customClassification || <ItalicText>No position specified</ItalicText>
                ) : (
                  <>
                    {cv.professionalDetails?.position?.name || <ItalicText>No position specified</ItalicText>}
                    {cv.professionalDetails?.grade?.name && ` • ${cv.professionalDetails.grade.name}`}
                  </>
                )}
              </EmployeeRole>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box>
              <CVCompletionGaugeCard id={`cv-completion-gauge-${cv.id}`} cv={cv} size={40} />
            </Box>
            <Chip 
              label={`v.${cv.version || 1}`} 
              size="small" 
              sx={{ 
                  bgcolor: '#EEF2FF',
                  color: '#4F46E5',
                  border: '1px solid #C7D2FE',
                  fontSize: '0.65rem',
                  height: '18px'
              }} 
            />
          </Box>
        </Box>

        {/* CV content */}
        <Box sx={{ mt: 0 }}>
          {/* Roles - only show if not using custom classification */}
          {!cv.professionalDetails?.useCustomClassification && (
            <>
              {cv.professionalDetails?.roles && cv.professionalDetails.roles.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {cv.professionalDetails.roles.slice(0, 3).map((role) => (
                    <Chip
                      key={role.id}
                      label={role.name}
                      size="small"
                      sx={{
                        bgcolor: '#F0FDF4',
                        color: '#15803D',
                        fontSize: '0.7rem',
                        height: '20px',
                        fontWeight: 500,
                        borderRadius: '12px',
                        border: '1px solid #DCFCE7',
                        '&:hover': {
                          bgcolor: '#DCFCE7',
                        }
                      }}
                    />
                  ))}
                  {cv.professionalDetails.roles.length > 3 && (
                    <Chip
                      label={`+${cv.professionalDetails.roles.length - 3}`}
                      size="small"
                      sx={{
                        bgcolor: '#F0FDF4',
                        color: '#15803D',
                        fontSize: '0.7rem',
                        height: '20px',
                        fontWeight: 500,
                        borderRadius: '12px',
                        border: '1px solid #DCFCE7',
                      }}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '0.75rem' }}>
                    No roles specified
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Location and date */}
          <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {(cv.professionalDetails?.country || cv.professionalDetails?.city) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748B', fontSize: '0.75rem' }}>
                <FaMapMarkerAlt style={{ marginRight: '6px', fontSize: '0.75rem' }} />
                {[cv.professionalDetails?.city, cv.professionalDetails?.country]
                  .filter(Boolean)
                  .join(', ')}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#94A3B8', fontSize: '0.75rem', fontStyle: 'italic' }}>
                <FaMapMarkerAlt style={{ marginRight: '6px', fontSize: '0.75rem' }} />
                No location specified
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748B', fontSize: '0.75rem' }}>
              <FaCalendarAlt style={{ marginRight: '6px', fontSize: '0.75rem' }} />
              Updated: {formatDate(cv.updatedAt)}
            </Box>
            {cv.code && (
              /* ADR-109: CV code as the third metadata row */
              <Tooltip title="CV code — the Candidate No. printed on generated documents. Click to copy.">
                <Box
                  onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(cv.code); }}
                  sx={{ display: 'flex', alignItems: 'center', color: '#64748B', fontSize: '0.75rem', cursor: 'pointer', width: 'fit-content' }}
                >
                  <FaBarcode style={{ marginRight: '6px', fontSize: '0.75rem' }} />
                  Code: {formatCvCode(cv.code)}
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* Skills */}
          {cv.skills && cv.skills.length > 0 ? (
            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {cv.skills.slice(0, 4).map((skill) => (
                <Tooltip key={skill.id} title={`${skill.name} - ${skill.level?.name || 'No level'}`}>
                  <Chip
                    label={skill.name}
                    size="small"
                    sx={{
                      bgcolor: skill.belongsToRadar ? COLORS.SECONDARY : '#EEF2FF',
                      color: skill.belongsToRadar ? 'white' : '#4F46E5',
                      fontSize: '0.7rem',
                      height: '18px',
                      fontWeight: 500,
                      borderRadius: '12px',
                      border: skill.belongsToRadar ? 'none' : '1px solid #C7D2FE',
                      '&:hover': {
                        bgcolor: skill.belongsToRadar ? COLORS.PRIMARY : '#DDD6FE',
                      }
                    }}
                  />
                </Tooltip>
              ))}
              {cv.skills.length > 4 && (
                <Chip
                  label={`+${cv.skills.length - 4}`}
                  size="small"
                  sx={{
                    bgcolor: '#EEF2FF',
                    color: '#4F46E5',
                    fontSize: '0.7rem',
                    height: '18px',
                    fontWeight: 500,
                    borderRadius: '12px',
                    border: '1px solid #C7D2FE',
                    '&:hover': {
                      bgcolor: '#DDD6FE',
                    }
                  }}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '0.75rem' }}>
                No skills added
              </Typography>
            </Box>
          )}
        </Box>
      </CVCardContent>
    </CVCard>
  );
};

export default CVCardItem; 